import { useEffect, useMemo, useState } from 'react';
import type { CwOpportunityTypeAliasDto } from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { useCwOpportunityTypeAliases, useSetCwOpportunityTypeAliases } from './api.js';

interface AliasRow {
  id: string;
  cwTypeName: string;
  serviceCategoryId: string;
}

let aliasSeq = 0;
function newAliasId(): string {
  aliasSeq += 1;
  return `opp-type-alias-${aliasSeq}`;
}

interface OpportunityTypeAliasEditorProps {
  /** Whether the parent modal is open (gates the query). */
  isOpen: boolean;
}

/**
 * Maps a ConnectWise opportunity type (a dropdown) onto a PSA service category ("Type").
 * On the opportunities sync, a CW opportunity's type sets the synced opportunity's
 * service category through these rows; an opportunity whose type has no row leaves the
 * service category null/unchanged (the sync never fails on a missing alias).
 */
export function OpportunityTypeAliasEditor({
  isOpen,
}: OpportunityTypeAliasEditorProps): JSX.Element {
  const configQuery = useCwOpportunityTypeAliases(isOpen);
  const saveAliases = useSetCwOpportunityTypeAliases();

  const [rows, setRows] = useState<AliasRow[]>([]);
  const config = configQuery.data;

  useEffect(() => {
    if (!config) return;
    setRows(
      config.aliases.map((alias) => ({
        id: newAliasId(),
        cwTypeName: alias.cwTypeName,
        serviceCategoryId: alias.serviceCategoryId,
      })),
    );
  }, [config]);

  const serviceCategories = config?.serviceCategories ?? [];
  const discoveredTypes = config?.discoveredTypes ?? [];
  const live = config?.live ?? false;

  const mappedNames = useMemo(
    () => new Set(rows.map((row) => row.cwTypeName.trim().toLowerCase()).filter(Boolean)),
    [rows],
  );
  const unmappedDiscovered = discoveredTypes.filter(
    (name) => !mappedNames.has(name.trim().toLowerCase()),
  );

  const updateRow = (id: string, patch: Partial<AliasRow>): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };
  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };
  const addRow = (cwTypeName = ''): void => {
    setRows((prev) => [...prev, { id: newAliasId(), cwTypeName, serviceCategoryId: '' }]);
  };

  const handleSave = (): void => {
    const aliases: CwOpportunityTypeAliasDto[] = rows
      .filter((row) => row.cwTypeName.trim() && row.serviceCategoryId)
      .map((row) => ({
        cwTypeName: row.cwTypeName.trim(),
        serviceCategoryId: row.serviceCategoryId,
      }));
    saveAliases.mutate({ aliases });
  };

  const datalistId = 'cw-opportunity-types';

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Service category from opportunity type</h3>
        <p className="mt-1 text-sm text-slate-500">
          Map each ConnectWise opportunity type to a PSA service category ("Type") below. On sync,
          an opportunity&apos;s type sets its service category — an opportunity whose type has no
          row leaves the service category unchanged.
        </p>
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading service categories…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-rose-600">Failed to load service categories.</p>
      ) : (
        <>
          {live ? (
            <p className="text-xs text-emerald-600">
              ConnectWise opportunity types loaded live from your connection.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              No ConnectWise opportunity types discovered yet. Configure the connection to load
              suggestions — you can still type a name.
            </p>
          )}

          <datalist id={datalistId}>
            {discoveredTypes.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {saveAliases.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Could not save the type aliases. Make sure each row targets a valid service category.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">No type aliases yet. Add one below.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">ConnectWise opportunity type</th>
                  <th className="py-2 pr-3 font-medium">PSA service category</th>
                  <th className="py-2 font-medium sr-only">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3 align-top">
                      <input
                        type="text"
                        list={datalistId}
                        value={row.cwTypeName}
                        placeholder="e.g. Consulting"
                        onChange={(e) => updateRow(row.id, { cwTypeName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.serviceCategoryId}
                        onChange={(e) => updateRow(row.id, { serviceCategoryId: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">— Select category —</option>
                        {serviceCategories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="mt-1.5 text-xs font-medium text-slate-400 hover:text-rose-600"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => addRow()}>
              Add alias
            </Button>
            {unmappedDiscovered.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                <span>Unmapped:</span>
                {unmappedDiscovered.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => addRow(name)}
                    className="rounded-full border border-slate-300 bg-white px-2 py-0.5 hover:border-brand-400 hover:text-brand-700"
                  >
                    {name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ml-auto">
              <Button onClick={handleSave} isLoading={saveAliases.isPending} disabled={!config}>
                Save types
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
